import { trackFormOperation } from '@utils/formOperationTracker';
import {
    IAddRow,
    IChange,
    IChangeAction,
    IChanges,
    IClearTables,
    IDeleteRow,
    IDictFilter,
    IDictFilters,
    IDocumentTable,
    IFormValues,
    ISqlProcedureMap,
    ISqlProcedureSetValue,
    ISetValue,
    IUpdateRow,
} from '@models/Forms/IForms';
import { IFieldElem } from '@models/IFormData';
import { FormulaManager } from '@utils/managers/FormulaManager';
import { UseFormReturn } from 'react-hook-form';
import { isIsoDateNoTimeString, isIsoDateString } from '@utils/documentUtils';
import { sendNotification } from '@srm/ui/lib/notifications';
import { DictionariesService } from '@services/DictionariesService';
import { filterGridRowsByScript, filterResponseVoid } from '@utils/dictionaryFilters';
import { parseRuDate, parseRuDateTime } from '@utils/date';
import { toLocalDate } from '@srm/ui/lib/toLocalDate';
import { loadMathJs } from '@utils/mathjsLoader';
import { deepEqual } from '@utils/helpers';
import { EnvironmentService } from '@services/EnvironmentService';
import { v4 as uuidv4 } from 'uuid';
import { replaceFormulaValue, substituteFormulaData } from './formulaStringLiteral';
import { mergeRowFormValue } from '@utils/mergeRowFormValue';
import { isEvaluatedSetType, resolveChangeAction } from './changeActions';
import { isDocumentFieldKey, type IChangeFieldBinding } from './ChangeRuntime';
import { createVariablesFormula } from './createVariablesFormula';

export interface ISavedChangeField {
    saved: boolean;
    value: any;
}

export type SaveChangeField = (
    item: ISetValue, table?: IDocumentTable, rowData?: any, value?: any,
) => void | ISavedChangeField | Promise<void | ISavedChangeField>;

export type InvokeSavedChange = (
    binding: IChangeFieldBinding, value: any, rowData?: any, activeTargets?: Set<string>, rowOldData?: any,
) => Promise<boolean>;

/** ChangeRuntime гасит цикл по ключу цели, но addRow с invokeChanges, чей обработчик снова
 *  делает addRow, каждый раз даёт новую строку (новый uuid) — ключ не повторяется. activeTargets
 *  хранит цепочку активных обработчиков, его размер — глубина каскада. */
export const MAX_SAVED_CHANGE_DEPTH = 32;
const assertCascadeDepth = (activeTargets: Set<string>, key: string) => {
    if (activeTargets.size >= MAX_SAVED_CHANGE_DEPTH) {
        throw new Error(`invokeChanges cascade exceeded ${MAX_SAVED_CHANGE_DEPTH} nested handlers at "${key}".`);
    }
};

/** Каскад сохранённого значения. Обработчик поля документа не получает строку таблицы-источника:
 *  с ней его addRow/updateRow/clearTable работали бы в контексте чужой строки. */
const invokeSavedTarget = (
    invoke: InvokeSavedChange,
    key: string,
    value: any,
    table: IDocumentTable | undefined,
    rowData: any,
    activeTargets: Set<string>,
    rowOldData: any,
) => {
    assertCascadeDepth(activeTargets, key);
    if (isDocumentFieldKey(key)) return invoke({ key }, value, undefined, activeTargets, undefined);
    return invoke({ key, tableKey: table?.key, rowId: rowData?.['|NUM']?.toString() }, value, rowData, activeTargets,
        rowOldData);
};

interface IPendingRowChange {
    key: string;
    tableKey: string;
    subTable?: string;
    parentRow?: any;
    row: any;
    oldRow?: any;
}

/** После merge/setValue RHF может заменить объекты: ищем живую строку по её ключу. */
const runSavedRowChanges = async (
    pending: IPendingRowChange[], fields: Record<string, IFieldElem>, formMethods: UseFormReturn<any>,
    invoke: InvokeSavedChange | undefined, activeTargets: Set<string>,
) => {
    if (!invoke) return;
    for (const target of pending) {
        const field = fields[target.tableKey];
        if (!field) continue;
        let rows = formMethods.getValues(field.name) as any[];
        let scopeId = field.name;
        if (!Array.isArray(rows)) continue;
        if (target.subTable) {
            const index = rows.findIndex((row) => row === target.parentRow ||
                (target.parentRow?.['|NUM'] != null && row['|NUM'] === target.parentRow['|NUM']));
            if (index < 0) continue;
            rows = rows[index][target.subTable];
            scopeId = `${scopeId}.${index}.${target.subTable}`;
        }
        const row = Array.isArray(rows) ? rows.find((row) => row === target.row ||
            (target.row['|NUM'] != null && row['|NUM'] === target.row['|NUM'])) : undefined;
        if (!row) continue;
        assertCascadeDepth(activeTargets, target.key);
        await invoke({ key: target.key, tableKey: target.subTable ?? target.tableKey,
            rowId: row['|NUM']?.toString(), scopeId }, row[target.key], row, activeTargets, target.oldRow);
    }
};

/**
 * Запись таблицы, прочитанной до await. Правки, сделанные за время ожидания (ввод в
 * форме строки, соседний каскад), устаревшая копия не затирает: поверх текущего
 * значения ложится только дельта самого правила. `before` — снимок массива на момент
 * чтения: RHF заменяет строки внутри живого массива.
 */
const writeTableDelta = (formMethods: UseFormReturn<any>, name: string, before: unknown, next: unknown) => {
    formMethods.setValue(name, mergeRowFormValue(before, next, formMethods.getValues(name)), { shouldDirty: true });
};

export const clearTables = (
    clearTables: IClearTables[],
    formMethods: UseFormReturn<any>,
    fields: Record<string, IFieldElem>,
    rowData?: any,
) => {
    clearTables?.forEach((item) => {
        if (item.table) {
            let table = fields[item.table];

            // clearTable table="|LinkDocument|..." без subTable:
            // Подтаблица линка может не существовать как отдельное поле в fields (родительский документ)
            // или маппиться на таблицу линков. Нужно очистить подтаблицу внутри строк линков.
            if (!item.subTable && !item.column && item.table.includes('|LinkDocument|')) {
                // Случай 1: поле не найдено в fields → ищем родительскую #links таблицу
                if (!table) {
                    const parentTableKey = Object.keys(fields).find((k) => k.startsWith('#links'));
                    if (parentTableKey) {
                        const parentTable = fields[parentTableKey];
                        const tableRows = (formMethods.getValues(parentTable.name) ?? []) as any[];
                        for (let i = 0; i < tableRows.length; i++) {
                            if (rowData) {
                                if (tableRows[i]['|NUM'] === rowData['|NUM']) {
                                    tableRows[i][item.table] = [];
                                    break;
                                }
                            } else {
                                tableRows[i][item.table] = [];
                            }
                        }
                        formMethods.setValue(parentTable.name, tableRows, { shouldDirty: true });
                    }
                    return;
                }

                // Случай 2: поле найдено, но значение — массив строк, содержащих item.table как свойство
                // (т.е. table.name указывает на таблицу линков, а не на подтаблицу)
                const currentValue = formMethods.getValues(table.name) as any[];
                if (Array.isArray(currentValue) && currentValue.length > 0 && item.table in currentValue[0]) {
                    for (let i = 0; i < currentValue.length; i++) {
                        if (rowData) {
                            if (currentValue[i]['|NUM'] === rowData['|NUM']) {
                                currentValue[i][item.table] = [];
                                break;
                            }
                        } else {
                            currentValue[i][item.table] = [];
                        }
                    }
                    formMethods.setValue(table.name, currentValue, { shouldDirty: true });
                    return;
                }

                // Случай 3: поле найдено, значение — сама подтаблица (EditTable контекст)
                // → проваливаемся в стандартную логику ниже
            }

            if (item.column) {
                let tableRows = (formMethods.getValues(table.name as any) ?? []) as any[];
                for (let index = 0; index < tableRows.length; index++) {
                    const row = tableRows[index];
                    if (item.subTable && rowData) {
                        if (row['|NUM'] === rowData['|NUM']) {
                            let subTableRows = row[item.subTable];
                            for (let subIndex = 0; subIndex < subTableRows.length; subIndex++) {
                                let columns = item.column.split(';');
                                columns.forEach((column) => {
                                    subTableRows[subIndex][column] = null;
                                });
                            }
                            row[item.subTable] = subTableRows;
                        }
                    } else if (item.subTable) {
                        // Без контекста строки правило уровня документа: колонки подтаблицы
                        // чистятся в каждой строке родительской таблицы, сами строки не трогаем.
                        const columns = item.column.split(';');
                        const subTableRows = (row[item.subTable] ?? []) as any[];
                        row[item.subTable] = subTableRows.map((subRow) => {
                            const next = { ...subRow };
                            columns.forEach((column) => {
                                next[column] = null;
                            });
                            return next;
                        });
                    } else {
                        let columns = item.column.split(';');
                        columns.forEach((column) => {
                            row[column] = null;
                        });
                    }
                    tableRows[index] = row;
                }
                formMethods.setValue(table.name as any, tableRows as any, { shouldDirty: true });
            } else {
                if (item.subTable && rowData) {
                    const tableRows = formMethods.getValues(table.name) as any[];
                    for (let index = 0; index < tableRows.length; index++) {
                        const row = tableRows[index];
                        if (row['|NUM'] === rowData['|NUM']) {
                            rowData[item.subTable] = [];
                            tableRows[index] = rowData;
                            formMethods.setValue(table.name, tableRows, { shouldDirty: false });
                            break;
                        }
                    }
                } else if (item.subTable) {
                    // Без контекста строки (правило уровня документа — смена дат договора) чистится
                    // подтаблица в каждой строке, а не родительская таблица: раньше здесь
                    // сносились сами лоты (EUPDEV-13029).
                    const tableRows = (formMethods.getValues(table.name) ?? []) as any[];
                    const subTable = item.subTable;
                    formMethods.setValue(
                        table.name,
                        tableRows.map((row) => ({ ...row, [subTable]: [] })),
                        { shouldDirty: true },
                    );
                } else {
                    formMethods.setValue(table.name as any, [] as any, { shouldDirty: true });
                }
            }
        }
    });
};

const getFormValues = (
    formDataParams: IFormValues,
    formMethods: UseFormReturn<any>,
    fields: Record<string, IFieldElem>,
) => {
    const map = {} as Record<string, any>;
    if (formDataParams) {
        for (const ele of formDataParams.formValue) {
            let field = fields[ele.attr];
            if (field === undefined) throw new Error(`Поле ${ele.attr} не найдено в перечне полей formValues`);
            let fname = ('fields.[' + field.index + '].value') as any;
            let val = formMethods.getValues(fname);
            if (ele.column) {
                if (ele.function === '{join}') {
                    map[ele.key] = (val as [])?.map((item: any) => `'${item[ele.column]}'`).join(',') ?? val;
                } else {
                    map[ele.key] = (val as [])?.map((item: any) => item[ele.column]) ?? val;
                }
            } else {
                map[ele.key] = val;
            }
        }
    }
    return JSON.stringify(map);
};

const getFiltersAsync = async (
    filters: IDictFilters,
    formMethods: UseFormReturn<any>,
    fields: Record<string, IFieldElem>,
) => {
    let result = {} as IDictFilter;
    if (filters) {
        for (const ele of filters.filter) {
            let conditionMng = new FormulaManager(ele.condition);
            conditionMng.Init(fields, formMethods);
            if (await conditionMng.EvalFormulaValues(true, true)) {
                result.condition = ele.condition;
                if (ele.filter) {
                    let filterMng = new FormulaManager(ele.filter);
                    filterMng.Init(fields, formMethods);
                    result.filter = (await filterMng.ReplaceFormulaValues(true, true))!;
                }
                if (ele.script) {
                    let scriptMng = new FormulaManager(ele.script);
                    scriptMng.Init(fields, formMethods);
                    result.script = (await scriptMng.ReplaceFormulaValues(true, true))!;
                }
            }
        }
    }
    return result;
};

/** Родительские строки-получатели `addRow` в подтаблицу. С контекстом строки — одна, по |NUM
 *  (если её ещё нет в таблице, она будет добавлена); без контекста — каждая строка родительской
 *  таблицы: так правило уровня документа (смена дат договора) дотягивается до подтаблиц всех лотов. */
const resolveParentRows = (tableRows: any[], parentRowData?: any): Array<{ index: number; row: any }> => {
    if (parentRowData) {
        const index = tableRows.findIndex((row) => row['|NUM'] === parentRowData['|NUM']);
        return [{ index, row: index >= 0 ? { ...tableRows[index] } : { ...parentRowData } }];
    }
    return tableRows.map((row, index) => ({ index, row: { ...row } }));
};

/** Проходит ли родительская строка `parentFilter` правила `addRow`. Формула считается по самой
 *  строке: `{|Колонка}`, `COUNT(|Подтаблица)`, `{[currentRow]|Колонка}` — всё о ней. */
const matchesParentFilter = async (
    filter: string | undefined,
    row: any,
    tableKey: string,
    formMethods: UseFormReturn<any>,
    fields: Record<string, IFieldElem>,
    rowOldData?: any,
): Promise<boolean> => {
    if (!filter) return true;
    const rule = replaceRowTags(filter, row, rowOldData);
    const filterMng = new FormulaManager(rule);
    filterMng.Init(fields, formMethods);
    const coll = filterMng.GetFields();
    const values = coll.length > 0 ? GetFormulaValues(coll, tableKey, fields, row, formMethods) : undefined;
    return !!(await filterMng.EvalFormulaValues(true, true, values));
};

/**
 * `parentTableKey` — ключ таблицы, из правила колонки которой пришёл вызов. С ним
 * `table="|LinkDocument|График_поставки"` (подтаблица строки, которой нет среди полей
 * документа) читается как `table="#links|…" subTable="|LinkDocument|График_поставки"`
 * для текущей строки — тот же addRow работает и из формы строки, где подтаблица
 * есть в полях, и из ячейки родительской таблицы.
 */
export const addRow = async (
    addRows: IAddRow[],
    formMethods: UseFormReturn<any>,
    fields: Record<string, IFieldElem>,
    rowData?: any,
    rowOldData?: any,
    parentTableKey?: string,
    invokeSavedChange?: InvokeSavedChange,
    activeTargets: Set<string> = new Set(),
) => {
    if (addRows) {
        for (let index = 0; index < addRows.length; index++) {
            const item = addRows[index];
            if (item.table) {
                const pending: IPendingRowChange[] = [];
                let tableKey = item.table;
                let subTable = item.subTable;
                let table = fields[tableKey];
                const parentRowData = rowData;
                if (!table && !subTable && parentRowData && parentTableKey && fields[parentTableKey]) {
                    subTable = item.table;
                    tableKey = parentTableKey;
                    table = fields[tableKey];
                }
                if (!table) {
                    console.warn(`addRow: таблица «${item.table}» не найдена среди полей формы`);
                    continue;
                }

                /** Число строк правила для контекста: поля строки («|LinkDocument|Дата_поставки_до»
                 *  в countRows из ячейки родительской таблицы) берутся из неё, без контекста —
                 *  значения по умолчанию из полей формы. */
                const resolveRowCount = async (contextRow?: any): Promise<number> => {
                    if (!item.countRows) return 1;
                    const count = new FormulaManager(item.countRows);
                    count.Init(fields, formMethods);
                    const coll = count.GetFields();
                    const values =
                        contextRow && coll.length > 0
                            ? GetFormulaValues(coll, tableKey, fields, contextRow, formMethods)
                            : undefined;
                    const result = Number(await count.EvalFormulaValues(true, false, values));
                    return Number.isFinite(result) && result > 0 ? Math.floor(result) : 0;
                };
                let externalRowCount: number | undefined;
                let tableRows = formMethods.getValues(table.name as any) as any[];
                let exRows: any[] = [];
                if (tableRows == undefined || tableRows == null) tableRows = [];
                const rowsBefore = [...tableRows];
                if (item.externalDataSource != null) {
                    let params =
                        '?formValues=' +
                        encodeURIComponent(
                            getFormValues(item.externalDataSource.formDataSource.formValues, formMethods, fields),
                        );

                    let response = await DictionariesService.getGridFormdataItems(
                        '-1',
                        item.externalDataSource.formDataSource.dictName,
                        params,
                    );
                    if (
                        item.externalDataSource.formDataSource.filters &&
                        item.externalDataSource.formDataSource.filters.filter
                    ) {
                        let filters = await getFiltersAsync(
                            item.externalDataSource.formDataSource.filters,
                            formMethods,
                            fields,
                        );
                        let _data = filterResponseVoid(response.data.data, filters);
                        if (filters?.script) {
                            _data = filterGridRowsByScript(_data, filters?.script);
                        }
                        exRows = _data;
                        externalRowCount = _data.length;
                    } else {
                        externalRowCount = response.data.totalCount;
                        exRows = response.data.data;
                    }
                }

                /** Строки правила для контекста `contextRow` (строка документа либо родительская строка). */
                const buildRows = async (contextRow: any) => {
                    const result: Record<string, any>[] = [];
                    const rowcount = externalRowCount ?? (await resolveRowCount(contextRow));
                    for (let indexRow = 0; indexRow < rowcount; indexRow++) {
                        const obj: Record<string, any> = {};
                        let rowDataForSet = contextRow;
                        if (exRows.length > 0) {
                            let exRowData: Record<string, any> = {};
                            exRows[indexRow].fields.forEach((item: any) => {
                                exRowData['|' + item.name] = item.value;
                            });
                            rowDataForSet = exRowData;
                        }
                        if (subTable && contextRow && rowDataForSet && contextRow !== rowDataForSet) {
                            rowDataForSet = { ...contextRow, ...rowDataForSet };
                        }
                        for (let index = 0; index < item.setValues.sets.length; index++) {
                            const element = item.setValues.sets[index];
                            let objCopy = { ...element };
                            const formula = element.attr.replace(/\{index\}/g, indexRow.toString());
                            objCopy.attr = replaceRowTags(formula, subTable ? contextRow : rowDataForSet, rowOldData,
                                isEvaluatedSetType(element.type));

                            obj[element.key] = await GetValueForSetValue(
                                objCopy,
                                undefined,
                                fields,
                                rowDataForSet,
                                formMethods,
                            );
                        }
                        if (obj['|NUM'] == null || obj['|NUM'] === '') {
                            obj['|NUM'] = uuidv4();
                        }
                        result.push(obj);
                        for (const set of item.setValues.sets) {
                            if (set.invokeChanges) pending.push({ key: set.key, tableKey, subTable,
                                parentRow: contextRow, row: obj });
                        }
                    }
                    return result;
                };

                if (subTable) {
                    const next = [...tableRows];
                    for (const parent of resolveParentRows(tableRows, parentRowData)) {
                        // С контекстом строки считаем по строке вызывающего (в ней может быть ещё
                        // не записанная правка ячейки), без него — по строке из таблицы.
                        const contextRow = parentRowData ?? parent.row;
                        const matches = await matchesParentFilter(
                            item.parentFilter,
                            contextRow,
                            tableKey,
                            formMethods,
                            fields,
                            rowOldData,
                        );
                        if (!matches) continue;
                        const subTableRows = Array.isArray(contextRow[subTable]) ? [...contextRow[subTable]] : [];
                        subTableRows.push(...(await buildRows(contextRow)));
                        parent.row[subTable] = subTableRows;
                        // Вызывающий из ячейки (commitRowPatch) пишет в форму СВОЮ строку поверх
                        // setValue ниже — без записи в неё новые строки подтаблицы терялись.
                        if (parentRowData) parentRowData[subTable] = subTableRows;
                        if (parent.index >= 0) {
                            next[parent.index] = parent.row;
                        } else {
                            next.push(parent.row);
                        }
                    }
                    tableRows = next;
                } else {
                    tableRows = [...tableRows, ...(await buildRows(rowData))];
                }

                writeTableDelta(formMethods, table.name, rowsBefore, tableRows);
                await runSavedRowChanges(pending, fields, formMethods, invokeSavedChange, activeTargets);
            }
        }
    }
};

const ROW_TAGS = {
    currentRow: /\{\[currentRow\](.*?)\}/,
    currentOldRow: /\{\[currentOldRow\](.*?)\}/,
} as const;

/**
 * Подстановка `{[currentRow]колонка}` / `{[currentOldRow]колонка}`; пустая ячейка — `0`.
 * Вне кавычек значение вставляется как есть: тег стоит и операндом (`{…} > 5`), и аргументом
 * функции (`OFFSETDAY(…;;{…})`, `GETDATAFROMSP(…;;{…})`), где кавычки сломали бы аргумент.
 * `evaluated` — выражение точно вычисляется (условие, фильтр, calc/date): внутри строки автора
 * (`'{[currentRow]|ИД}'`) подставляется экранированное содержимое, кавычка из данных не
 * закрывает строку. В тексте (text-набор) кавычки автора — часть результата, там как есть.
 */
const replaceTagRow = (formula: string, tag: keyof typeof ROW_TAGS, row: any, evaluated: boolean) => {
    const cellText = (column: string) => (row?.[column] != null ? String(row[column]) : '0');
    if (evaluated) {
        return substituteFormulaData(formula, ROW_TAGS[tag], (_match, [column], quote) => {
            if (!column) return undefined;
            return quote ? { text: cellText(column) } : { raw: cellText(column) };
        });
    }
    // Замена функцией: `$&`/`$'` из данных не разворачиваются; значение не сканируется повторно.
    return formula.replace(new RegExp(ROW_TAGS[tag].source, 'g'), (match: string, column: string) =>
        column ? cellText(column) : match);
};

const replaceRowTags = (formula: string, row: any, oldRow: any, evaluated = true) =>
    replaceTagRow(replaceTagRow(formula, 'currentRow', row, evaluated), 'currentOldRow', oldRow, evaluated);

/**
 * Совпадают ли значения ячейки по смыслу. Пустые значения (`null`/`undefined`/`''`) равны
 * между собой, числа сравниваются как числа (`100` и `'100.00'`), остальное — по строковому
 * представлению; объекты и массивы — глубоким сравнением.
 */
export const isSameCellValue = (current: unknown, next: unknown): boolean => {
    if (current === next) return true;

    const currentEmpty = current === null || current === undefined || current === '';
    const nextEmpty = next === null || next === undefined || next === '';
    if (currentEmpty || nextEmpty) return currentEmpty && nextEmpty;

    if (typeof current === 'object' || typeof next === 'object') return deepEqual(current, next);

    const currentNumber = Number(current);
    const nextNumber = Number(next);
    if (Number.isFinite(currentNumber) && Number.isFinite(nextNumber)) return currentNumber === nextNumber;

    return String(current) === String(next);
};

/**
 * Фильтр строк updateRow/deleteRow. Теги `{[currentRow]…}` берутся из строки-инициатора, а не
 * из проверяемой, поэтому подстановка и разбор формулы — один раз на правило; от проверяемой
 * строки зависят только значения полей (как в фактах таблиц).
 */
const prepareRowFilter = (
    filter: string | undefined,
    fields: Record<string, IFieldElem>,
    formMethods: UseFormReturn<any>,
    rowData: any,
    rowOldData: any,
    isEditNew = false,
) => {
    if (!filter) return undefined;
    const filterMng = new FormulaManager(replaceRowTags(filter, rowData, rowOldData));
    filterMng.Init(fields, formMethods);
    const coll = filterMng.GetFields();
    return async (row: any, tableKey: string | undefined) => {
        const values = formMethods && row && coll.length > 0
            ? GetFormulaValues(coll, tableKey, fields, row, formMethods) : undefined;
        return filterMng.EvalFormulaValues(isEditNew, isEditNew, values);
    };
};

const processingUpdateRow = async (
    tableRows: any[],
    item: IUpdateRow,
    formMethods: UseFormReturn<any>,
    fields: Record<string, IFieldElem>,
    rowData?: any,
    rowOldData?: any,
    pending: IPendingRowChange[] = [],
    parentRow?: any,
) => {
    // Строка копируется только при настоящем изменении: нетронутые сохраняют ссылку, и
    // сравнение/слияние таблицы (updateRow, mergeRowFormValue) проходит их по ===.
    const newArr: any[] = [...tableRows];
    let copied = false;
    const filter = prepareRowFilter(item.filter, fields, formMethods, rowData, rowOldData);

    for (let indexRow = 0; indexRow < newArr.length; indexRow++) {
        let row = newArr[indexRow];

        const checkResult = filter ? await filter(row, item.table) : true;

        if (checkResult) {
            for (let index = 0; index < item.setValues.sets.length; index++) {
                const set = item.setValues.sets[index];
                let objCopy = { ...set };
                const formula = set.attr.replace(/\{index\}/g, indexRow.toString());
                objCopy.attr = replaceRowTags(formula, rowData ? rowData : row, rowOldData, isEvaluatedSetType(set.type));

                const nextValue = await GetValueForSetValue(objCopy, undefined, fields, row, formMethods);
                // Правило пересчитывает поле в КАЖДОЙ подходящей строке, и на таблице-линков
                // безусловное присваивание делало «изменённой» всю таблицу от правки одной
                // ячейки: строки уходили на бэк с |dirty и он пересохранял каждый связанный
                // документ (на заявке с 1500 позициями — минуты). Пишем только настоящие
                // изменения; совпадение по смыслу ('' и null, 100 и '100') не трогаем, чтобы
                // не менять представление значения.
                // Каскад invokeChanges — тоже только от настоящего изменения: иначе правка одной
                // ячейки запускала обработчик в каждой подходящей строке таблицы.
                if (isSameCellValue(row[set.key], nextValue)) continue;
                if (row === tableRows[indexRow]) {
                    row = { ...row };
                    newArr[indexRow] = row;
                    copied = true;
                }
                row[set.key] = nextValue;
                if (set.invokeChanges) pending.push({ key: set.key, tableKey: item.table,
                    subTable: item.subTable, parentRow, row, oldRow: tableRows[indexRow] });
            }
        }
    }
    return copied ? newArr : tableRows;
};
const processingDeleteRow = async (
    tableRows: any[],
    item: IDeleteRow,
    formMethods: UseFormReturn<any>,
    fields: Record<string, IFieldElem>,
    rowData?: any,
    rowOldData?: any,
) => {
    const filter = prepareRowFilter(item.filter, fields, formMethods, rowData, rowOldData, true);
    if (!filter) return tableRows;
    const newArr: any[] = [];
    for (const row of tableRows) {
        // Оставшиеся строки не копируем: слияние таблицы проходит их по ===.
        if (!(await filter(row, undefined))) newArr.push(row);
    }
    return newArr.length === tableRows.length ? tableRows : newArr;
};

const updateRowsGroup = async (
    updateRowsGroup: any,
    tableRows: any,
    formMethods: UseFormReturn<any>,
    fields: Record<string, IFieldElem>,
    rowData?: any,
    rowOldData?: any,
    pending: IPendingRowChange[] = [],
) => {
    let rows = Array.isArray(tableRows) ? [...tableRows] : [];
    // Стартуем с исходного массива, чтобы при пустых tableRows вернуть [], а не undefined.
    // Иначе setValue() в updateRow обнуляет поле таблицы — и #links|... сериализуется без value.
    let result: any = rows;
    for (let index = 0; index < updateRowsGroup.length; index++) {
        const item = updateRowsGroup[index];
        if (item.table) {
            if (rows != undefined && rows != null && rows.length > 0) {
                if (item.subTable) {
                    const parent: any[] = [...rows];
                    for (let indexRow = 0; indexRow < parent.length; indexRow++) {
                        const row = parent[indexRow];
                        const subTableRows = row[item.subTable];
                        if (subTableRows != undefined) {
                            const pendingFrom = pending.length;
                            const nextSubRows = await processingUpdateRow(
                                subTableRows,
                                item,
                                formMethods,
                                fields,
                                rowData,
                                rowOldData,
                                pending,
                                row,
                            );
                            if (nextSubRows !== subTableRows) {
                                const nextRow = { ...row, [item.subTable]: nextSubRows };
                                parent[indexRow] = nextRow;
                                for (let p = pendingFrom; p < pending.length; p++) pending[p].parentRow = nextRow;
                            }
                        }
                    }
                    result = parent;
                } else {
                    result = await processingUpdateRow(rows, item, formMethods, fields, rowData, rowOldData, pending);
                }
                rows = result;
            }
        }
    }
    return result;
};

export const updateRow = async (
    updateRows: IUpdateRow[],
    formMethods: UseFormReturn<any>,
    fields: Record<string, IFieldElem>,
    rowData?: any,
    rowOldData?: any,
    invokeSavedChange?: InvokeSavedChange,
    activeTargets: Set<string> = new Set(),
) => {
    if (updateRows) {
        let groups = updateRows.reduce(
            (entryMap, e) => entryMap.set(e.table, [...(entryMap.get(e.table) || []), e]),
            new Map(),
        );

        for (const [key, rowsGroup] of groups) {
            if (key) {
                let table = fields[key];
                let tableRows = formMethods.getValues(table.name as any) as any[];
                const rowsBefore = Array.isArray(tableRows) ? [...tableRows] : tableRows;

                const pending: IPendingRowChange[] = [];
                const result = await updateRowsGroup(rowsGroup, tableRows, formMethods, fields, rowData, rowOldData, pending);
                // Строка заменяется только настоящим изменением, нетронутые сохраняют ссылку.
                const changed =
                    !Array.isArray(tableRows) ||
                    result.length === 0 ||
                    result.length !== rowsBefore.length ||
                    result.some((row: any, index: number) => row !== rowsBefore[index]);
                if (changed) {
                    writeTableDelta(formMethods, table.name, rowsBefore, result);
                }
                await runSavedRowChanges(pending, fields, formMethods, invokeSavedChange, activeTargets);
            }
        }
    }
};
export const deleteRow = async (
    deleteRows: IDeleteRow[],
    formMethods: UseFormReturn<any>,
    fields: Record<string, IFieldElem>,
    rowData?: any,
    rowOldData?: any,
) => {
    if (deleteRows) {
        for (let index = 0; index < deleteRows.length; index++) {
            const item = deleteRows[index];
            if (item.table) {
                let table = fields[item.table];
                let tableRows = formMethods.getValues(table.name as any) as any[];

                if (tableRows != undefined && tableRows != null && tableRows.length > 0) {
                    const rowsBefore = [...tableRows];
                    let result: any[] = tableRows;
                    if (item.subTable) {
                        const parent: any[] = [...tableRows];
                        for (let indexRow = 0; indexRow < parent.length; indexRow++) {
                            const row = parent[indexRow];
                            const subTableRows = row[item.subTable];
                            if (!Array.isArray(subTableRows)) continue;
                            const kept = await processingDeleteRow(
                                subTableRows,
                                item,
                                formMethods,
                                fields,
                                rowData,
                                rowOldData,
                            );
                            if (kept !== subTableRows) {
                                parent[indexRow] = { ...row, [item.subTable]: kept };
                                result = parent;
                            }
                        }
                    } else {
                        result = await processingDeleteRow(tableRows, item, formMethods, fields, rowData, rowOldData);
                    }

                    // Ничего не удалено — не пишем: setValue с shouldDirty пометил бы форму изменённой.
                    if (result !== tableRows) writeTableDelta(formMethods, table.name, rowsBefore, result);
                }
            }
        }
    }
};

export const GetValueForSetValue = async (
    item: ISetValue,
    tableName: any,
    data: Record<string, IFieldElem>,
    rowData?: any,
    formMethods?: UseFormReturn<any>,
) => {
    let val: any;

    if (item.type === 'json') {
        return JSON.parse(item.attr);
    }
    let formulaMng = new FormulaManager(item.attr);
    formulaMng.Init({ ...data, ...rowData }, formMethods, rowData);
    let coll = formulaMng.GetFields();
    let values: any[] | undefined = undefined;
    if (formMethods && rowData && coll.length > 0 && !coll.every((element) => element === undefined)) {
        values = GetFormulaValues(coll, tableName, data, rowData, formMethods);
    }

    switch (item.type) {
        case 'text':
            val = await formulaMng.ReplaceFormulaValues(true, true, values, true);
            break;
        case 'json':
            val = JSON.parse(item.attr);
            break;
        case 'calc': {
            try {
                let valCalc = await formulaMng.EvalFormulaValues(true, true, values);
                if (isNaN(valCalc) && typeof valCalc !== 'string') {
                    val = 0;
                } else {
                    if (typeof valCalc == 'string' && valCalc.indexOf(',')) {
                        valCalc = valCalc.replace(',', '.');
                    }
                    val = +valCalc;
                    if (item.floatPoints) {
                        const { round } = await loadMathJs();
                        val = round(val, item.floatPoints);
                    }
                }
            } catch (error) {
                console.log(error);
                val = 0;
            }
            break;
        }
        case 'date': {
            try {
                val = await formulaMng.EvalFormulaValues(true, true, values);
                if (val == null || val === '') {
                    val = null;
                } else if (val instanceof Date) {
                    val = !isNaN(val.getTime()) && val.getTime() > 0 ? val : null;
                } else if (typeof val === 'number') {
                    // Unix ms от OFFSETDAY/OFFSETMONTH/OFFSETYEAR/SETDATE/NOW/NowOnlyDate/StrToDate:
                    // formatFieldValueByType приводит ISO-строку к getTime().toString(),
                    // mathjs.evaluate затем возвращает её как number.
                    const d = new Date(val);
                    val = !isNaN(d.getTime()) && d.getTime() > 0 ? d : null;
                } else if (typeof val !== 'string') {
                    val = null;
                } else if (/^\d{1,2}\.\d{1,2}\.\d{4}/.test(val)) {
                    // Русский формат dd.MM.yyyy[ HH:mm:ss|HH.mm.ss] — не отдаём V8 Date(),
                    // он интерпретирует как MM.DD.YYYY (silent-wrong для DD≤12).
                    const hasTime = val.split(' ').length > 1;
                    const parsed = hasTime ? parseRuDateTime(val) : parseRuDate(val);
                    if (parsed) {
                        val = parsed;
                    } else {
                        const fallback = toLocalDate(val);
                        val = !isNaN(fallback.getTime()) ? fallback : null;
                    }
                } else {
                    const parseVal = new Date(val);
                    if (!isNaN(parseVal.getTime()) && parseVal.getTime() > 0) {
                        val = parseVal;
                    } else {
                        const fallback = toLocalDate(val);
                        val = !isNaN(fallback.getTime()) ? fallback : null;
                    }
                }
            } catch (error) {
                val = null;
            }
            break;
        }
        default:
            val = item.attr;
            break;
    }
    return val;
};

const normalizeSqlProcedureValue = (value: any, type?: string) => {
    if (value === undefined || value === null) {
        return null;
    }

    switch (type) {
        case 'calc': {
            const normalized = typeof value === 'string' ? value.replace(',', '.') : value;
            const parsed = Number(normalized);
            return Number.isNaN(parsed) ? 0 : parsed;
        }
        case 'date':
        case 'datetime':
        case 'nosecdatetime': {
            if (value === '') {
                return null;
            }
            const parsed = value instanceof Date ? value : new Date(value);
            return !Number.isNaN(parsed.getTime()) && parsed.getTime() > 0 ? parsed : null;
        }
        default:
            return value;
    }
};

const getSqlProcedureParams = async (
    sqlProcedure: ISqlProcedureSetValue,
    fields: Record<string, IFieldElem>,
    formMethods: UseFormReturn<any>,
    rowData?: any,
) => {
    const params: Record<string, any> = {};

    if (sqlProcedure.params) {
        for (let index = 0; index < sqlProcedure.params.length; index++) {
            const param = sqlProcedure.params[index];
            let docId = fields ? ((fields['doc_Id']?.value as string) ?? null) : null;
            params[param.key] =
                param.attr == 'doc_Id'
                    ? docId
                    : await GetValueForSetValue(param, undefined, fields, rowData, formMethods);
        }
    }

    return params;
};

const setMappedScalarValue = (
    map: ISqlProcedureMap,
    value: any,
    fields: Record<string, IFieldElem>,
    formMethods: UseFormReturn<any>,
    rowData?: any,
) => {
    const normalizedValue = normalizeSqlProcedureValue(value, map.type);

    // Колонка строки inline-таблицы (EditTable) живёт в объекте строки rowData.
    if (rowData && !isDocumentFieldKey(map.target)) {
        rowData[map.target] = normalizedValue;
        return { saved: true, value: rowData[map.target] };
    }

    // Поля документа (|Document|...) и колонки строки в custom-form (TableForm)
    // существуют как плоские поля react-hook-form — пишем через formMethods.
    const field = map.target ? fields?.[map.target] : undefined;
    if (field) {
        formMethods.setValue(field.name as any, normalizedValue, { shouldDirty: true });
        return { saved: true, value: formMethods.getValues(field.name) };
    }
    return { saved: false, value: undefined };
};

const executeSqlProcedures = async (
    sqlProcedures: ISqlProcedureSetValue[],
    fields: Record<string, IFieldElem>,
    formMethods: UseFormReturn<any>,
    setError: (errors?: string[]) => void,
    rowData?: any,
    table?: IDocumentTable,
    invokeSavedChange?: InvokeSavedChange,
    activeTargets: Set<string> = new Set(),
    rowOldData?: any,
) => {
    if (!sqlProcedures) {
        return true;
    }

    for (let index = 0; index < sqlProcedures.length; index++) {
        const sqlProcedure = sqlProcedures[index];
        let response: Awaited<ReturnType<typeof EnvironmentService.getExtendDataPost>>;
        try {
            const params = await getSqlProcedureParams(sqlProcedure, fields, formMethods, rowData);
            response = await EnvironmentService.getExtendDataPost({
                procedureName: sqlProcedure.name,
                encrypted: true,
                params,
            });
        } catch (error: any) {
            const message =
                error?.response?.data?.message ??
                error?.response?.data ??
                error?.message ??
                'Stored procedure call error';
            setError([String(message)]);
            return false;
        }

        const result = response.data ?? [];

        if (sqlProcedure.resultType === 'table') {
            const tableField = fields[sqlProcedure.target];
            if (!tableField) {
                continue;
            }

            const rows = result.map((row) => {
                const mappedRow: Record<string, any> = {};
                sqlProcedure.mapping?.forEach((map) => {
                    mappedRow[map.target] = normalizeSqlProcedureValue(row?.[map.source], map.type);
                });
                if (sqlProcedure.mapping?.some((map) => map.invokeChanges) && mappedRow['|NUM'] == null) {
                    mappedRow['|NUM'] = uuidv4();
                }
                return mappedRow;
            });

            formMethods.setValue(tableField.name as any, rows as any, { shouldDirty: true });
            const pending = rows.flatMap((row) => (sqlProcedure.mapping ?? [])
                .filter((map) => map.invokeChanges)
                .map((map) => ({ key: map.target, tableKey: sqlProcedure.target, row })));
            await runSavedRowChanges(pending, fields, formMethods, invokeSavedChange, activeTargets);
        } else {
            const firstRow = result[0];
            if (!firstRow) {
                continue;
            }

            for (const map of sqlProcedure.mapping ?? []) {
                const saved = setMappedScalarValue(map, firstRow?.[map.source], fields, formMethods, rowData);
                if (map.invokeChanges && saved.saved && invokeSavedChange) {
                    await invokeSavedTarget(invokeSavedChange, map.target, saved.value, table, rowData, activeTargets,
                        rowOldData);
                }
            }
        }

    }

    return true;
};

export const GetFormulaValues = (
    valuesFields: string[],
    tableName: any,
    data: Record<string, IFieldElem>,
    rowData?: any,
    formMethods?: UseFormReturn<any>,
    valField?: any,
) => {
    let values: any[] = [];
    if (formMethods) {
        valuesFields.forEach((field) => {
            field = field.startsWith('{') && field.endsWith('}') ? field.substring(1, field.length - 1) : field;
            let val: any = undefined;
            if (field === "'{value}'") {
                val = valField;
            } else if (
                field.includes('|Document') ||
                (field.startsWith('|Parent|') && !!data[field]) ||
                field.startsWith('#links') ||
                (!field.startsWith('|') && !field.includes('{')) ||
                rowData === undefined
            ) {
                val = formMethods.getValues(data[field].name);
                if (rowData != undefined && field == tableName) {
                    // Подстановка текущей строки нужна только ЗНАЧЕНИЮ для вычисления формулы —
                    // живой массив RHF не мутируем: мутация делала последующий setValue тем же
                    // содержимым deep-equal (RHF гасит его без события), и version-key правил
                    // (useFormulaWatchKey) не видел изменения таблицы.
                    let arr = [...((val as any[]) ?? [])];
                    let indexRow = arr.findIndex((f) => {
                        return f['|NUM'] == rowData['|NUM'];
                    });
                    if (indexRow == -1) {
                        arr.push(rowData);
                    } else {
                        arr[indexRow] = rowData;
                    }
                    val = arr;
                }
            } else if (field.startsWith('|')) {
                val = rowData[field];
            }
            values?.push(val);
        });
    }
    return values;
};

export const checkCondition = async (
    condition: string,
    value: any,
    fieldName: string,
    isEdit: boolean,
    isNew: boolean,
    data: Record<string, IFieldElem>,
    formMethods?: UseFormReturn<any>,
    rowData?: any,
    rowOldData?: any,
) => {
    let val = value;
    if (val instanceof Date) {
        val = val.getTime().toString();
    } else {
        if (isIsoDateString(val) || isIsoDateNoTimeString(val)) {
            let valDate = new Date(val); // Moment(value).toDate();
            val = valDate.getTime().toString();
        }
    }

    let rule = condition;
    if (rule.indexOf('{value}') > -1) {
        // Дата уже приведена к числовому timestamp; текст должен оставаться
        // строкой и в новых условиях без кавычек, и в legacy-шаблонах.
        const conditionValue = val !== value ? Number(val) : val;
        rule = replaceFormulaValue(rule, conditionValue);
    }
    // После {value}: её литерал не порождает тегов строки, а сырое значение ячейки
    // вне кавычек не сбивает разбор кавычек для {value}.
    rule = replaceRowTags(rule, rowData, rowOldData);
    let formulaManager = new FormulaManager(rule);
    formulaManager.Init(data, formMethods);
    let valuesFields = formulaManager.GetFields();

    let values: any[] | undefined = undefined;
    if (formMethods && valuesFields.length > 0) {
        values = GetFormulaValues(valuesFields, fieldName, data, rowData, formMethods, val);
    }

    return !!(await formulaManager.EvalFormulaValues(isEdit, isNew, values));
};

export const executeChanges = async (
    change: IChange,
    data: Record<string, IFieldElem>,
    onSave: SaveChangeField,
    setError: (errors?: string[]) => void,
    table?: IDocumentTable,
    formMethods?: UseFormReturn<any>,
    rowData?: any,
    rowOldData?: any,
    invokeSavedChange?: InvokeSavedChange,
    activeTargets: Set<string> = new Set<string>(),
) => {
    if (change?.setValues?.sets) {
        for (let index = 0; index < change?.setValues?.sets.length; index++) {
            const element = change?.setValues?.sets[index];
            let objCopy = { ...element };
            const formula = element.attr.replace(/\{index\}/g, index.toString());
            objCopy.attr = replaceRowTags(formula, rowData, rowOldData, isEvaluatedSetType(element.type));

            const saved = await onSave(objCopy, table, rowData);
            if (element.invokeChanges && invokeSavedChange && (!saved || saved.saved !== false)) {
                const field = data[element.key];
                const savedValue = saved ? saved.value : rowData && !isDocumentFieldKey(element.key)
                    ? rowData[element.key] : field && formMethods ? formMethods.getValues(field.name as any) : field?.value;
                await invokeSavedTarget(invokeSavedChange, element.key, savedValue, table, rowData, activeTargets,
                    rowOldData);
            }
        }
    }
    clearTables(change?.setValues?.clearTables!, formMethods!, data, rowData);
    await deleteRow(change?.setValues?.deleteRows!, formMethods!, data, rowData, rowOldData);
    await addRow(change?.setValues?.addRows!, formMethods!, data, rowData, rowOldData, table?.key, invokeSavedChange, activeTargets);
    await updateRow(change?.setValues?.updateRows!, formMethods!, data, rowData, rowOldData, invokeSavedChange, activeTargets);
    const sqlProceduresExecuted = await executeSqlProcedures(
        change?.setValues?.sqlProcedures!,
        data,
        formMethods!,
        setError,
        rowData,
        table,
        invokeSavedChange,
        activeTargets,
        rowOldData,
    );
    if (!sqlProceduresExecuted) {
        return false;
    }
    if (change?.showErrors) {
        let messages: string[] = [];
        change?.showErrors.messages.forEach((msg) => {
            messages.push(msg.value);
        });
        setError(messages);
    }
    if (change?.showNotifications) {
        for (let index = 0; index < change?.showNotifications.notifications.length; index++) {
            const msg = change?.showNotifications.notifications[index];
            let txt = await GetTextForNotification(msg.text, table?.key, data, rowData, formMethods);
            if (txt) {
                sendNotification(
                    {
                        title: msg.title,
                        message: txt,
                        variant: msg.type,
                    },
                    msg.disabledAutoHide ? 100000 : undefined,
                );
            }
        }
    }
    return true;
};

export const GetTextForNotification = async (
    text: string,
    tableName: any,
    data: Record<string, IFieldElem>,
    rowData?: any,
    formMethods?: UseFormReturn<any>,
) => {
    if (text.indexOf('{') > -1 || text.indexOf('(') > -1) {
        let formulaMng = new FormulaManager(text);
        formulaMng.Init(data, formMethods);
        let coll = formulaMng.GetFields();
        let values: any[] | undefined = undefined;
        if (formMethods && rowData && coll.length > 0 && !coll.every((element) => element === undefined)) {
            values = GetFormulaValues(coll, tableName, data, rowData, formMethods);
        }

        return await formulaMng.ReplaceFormulaValues(true, true, values, true);
    } else {
        return text;
    }
};

export const sortChanges = (changes: IChange[]) => {
    let newArr: IChange[] = [];
    changes.forEach((obj: any) => {
        const objCopy = { ...obj };
        newArr.push(objCopy);
    });
    return newArr.sort((a, b) => (a.index > b.index || a.condition == null ? 1 : -1));
};

export const ChangesFieldValue = (
    changes: IChanges,
    value: any,
    fieldName: string,
    data: Record<string, IFieldElem>,
    isEdit: boolean,
    isNew: boolean,
    onSave: SaveChangeField,
    setError: (errors?: string[]) => void,
    table?: IDocumentTable,
    rowData?: any,
    formMethods?: UseFormReturn<any>,
    rowOldData?: any,
    invokeSavedChange?: InvokeSavedChange,
    activeTargets: Set<string> = new Set<string>(),
    changeActions?: ReadonlyMap<string, IChangeAction>,
    actionPath: readonly string[] = [],
): Promise<void> => {
    if (!changes?.change?.length) return Promise.resolve();
    return trackFormOperation(formMethods, async () => {

        const sortedChanges = sortChanges(changes.change);
        for (let index = 0; index < sortedChanges.length; index++) {
            const change = sortedChanges[index];
            let needExecute = true;
            let subChanges = change.changes;
            let nestedActionPath = actionPath;
            if (change.condition) {
                needExecute = await checkCondition(
                    change.condition,
                    value,
                    fieldName,
                    isEdit,
                    isNew,
                    data,
                    formMethods,
                    rowData,
                    rowOldData,
                );
                if (!needExecute) {
                    subChanges = change.elseChanges?.changes;
                }
            }
            if (needExecute && change.callAction) {
                const { name, args } = change.callAction;
                if (change.setValues || change.showErrors || change.showNotifications || change.changes) {
                    throw new Error(`callAction "${name}" must be the only payload in its change.`);
                }
                const action = changeActions?.get(name);
                if (!action) throw new Error(`Unknown changeAction "${name}".`);
                if (actionPath.includes(name)) {
                    throw new Error(`Circular changeAction call: ${[...actionPath, name].join(' -> ')}.`);
                }
                const globalVariables = data.variables?.value && typeof data.variables.value === 'object'
                    ? data.variables.value as Record<string, unknown> : {};
                const variablesFormula = createVariablesFormula(data, formMethods, rowData);
                subChanges = await resolveChangeAction(action, args, value, globalVariables,
                    async (expression, variables) => {
                        const formula = variablesFormula(expression, variables);
                        const fields = formula.GetFields();
                        const values = formMethods && fields.length > 0
                            ? GetFormulaValues(fields, fieldName, data, rowData, formMethods, value) : undefined;
                        return formula.EvalFormulaValues(isEdit, isNew, values, true);
                    },
                    (expression) => replaceRowTags(expression, rowData, rowOldData),
                );
                nestedActionPath = [...actionPath, name];
            } else if (needExecute) {
                const executed = await executeChanges(
                    change,
                    data,
                    onSave,
                    setError,
                    table,
                    formMethods,
                    rowData,
                    rowOldData,
                    invokeSavedChange,
                    activeTargets,
                );
                if (!executed) {
                    continue;
                }
            }
            if (subChanges?.change?.length) {
                await ChangesFieldValue(
                    subChanges,
                    value,
                    fieldName,
                    data,
                    isEdit,
                    isNew,
                    onSave,
                    setError,
                    table,
                    rowData,
                    formMethods,
                    rowOldData,
                    invokeSavedChange,
                    activeTargets,
                    changeActions,
                    nestedActionPath,
                );
            }
        }
    });
};
